function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module utils/keystrokehandler
 */
import DomEmitterMixin from './dom/emittermixin';
import { getCode, parseKeystroke } from './keyboard';
/**
 * Keystroke handler allows registering callbacks for given keystrokes.
 *
 * The most frequent use of this class is through the {@link module:core/editor/editor~Editor#keystrokes `editor.keystrokes`}
 * property. It allows listening to keystrokes executed in the editing view:
 *
 *		editor.keystrokes.set( 'Ctrl+A', ( keyEvtData, cancel ) => {
 *			console.log( 'Ctrl+A has been pressed' );
 *			cancel();
 *		} );
 *
 * However, this utility class can be used in various part of the UI. For instance, a certain {@link module:ui/view~View}
 * can use it like this:
 *
 *		class MyView extends View {
 *			constructor() {
 *				this.keystrokes = new KeystrokeHandler();
 *
 * 				this.keystrokes.set( 'tab', handleTabKey );
 *			}
 *
 *			render() {
 *				super.render();
 *
 *				this.keystrokes.listenTo( this.element );
 *			}
 *		}
 *
 * That keystroke handler will listen to `keydown` events fired in this view's main element.
 *
 */

var KeystrokeHandler = /*#__PURE__*/function () {
  /**
   * Creates an instance of the keystroke handler.
   */
  function KeystrokeHandler() {
    _classCallCheck(this, KeystrokeHandler);

    /**
     * Listener used to listen to events for easier keystroke handler destruction.
     *
     * @protected
     * @member {module:utils/dom/emittermixin~Emitter}
     */
    this._listener = Object.create(DomEmitterMixin);
  }
  /**
   * Starts listening for `keydown` events from a given emitter.
   *
   * @param {module:utils/emittermixin~Emitter} emitter
   */


  _createClass(KeystrokeHandler, [{
    key: "listenTo",
    value: function listenTo(emitter) {
      var _this = this;

      // The #_listener works here as a kind of dispatcher. It groups the events coming from the same
      // keystroke so the listeners can be attached to them with different priorities.
      //
      // E.g. all the keystrokes with the `keyCode` of 42 coming from the `emitter` are propagated
      // as a `_keydown:42` event by the `_listener`. If there's a callback created by the `set`
      // method for this 42 keystroke, it listens to the `_listener#_keydown:42` event only and interacts
      // only with other listeners of this particular event, thus making it possible to prioritize
      // the listeners and safely cancel execution, when needed. Instead of duplicating the Emitter logic,
      // the KeystrokeHandler re–uses it to do its job.
      this._listener.listenTo(emitter, 'keydown', function (evt, keyEvtData) {
        _this._listener.fire('_keydown:' + getCode(keyEvtData), keyEvtData);
      });
    }
    /**
     * Registers a handler for the specified keystroke.
     *
     * @param {String|Array.<String|Number>} keystroke Keystroke defined in a format accepted by
     * the {@link module:utils/keyboard~parseKeystroke} function.
     * @param {Function} callback A function called with the
     * {@link module:engine/view/observer/keyobserver~KeyEventData key event data} object and
     * a helper funcion to call both `preventDefault()` and `stopPropagation()` on the underlying event.
     * @param {Object} [options={}] Additional options.
     * @param {module:utils/priorities~PriorityString|Number} [options.priority='normal'] The priority of the keystroke
     * callback. The higher the priority value the sooner the callback will be executed. Keystrokes having the same priority
     * are called in the order they were added.
     */

  }, {
    key: "set",
    value: function set(keystroke, callback) {
      var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var keyCode = parseKeystroke(keystroke);
      var priority = options.priority; // Execute the passed callback on KeystrokeHandler#_keydown.
      // TODO: https://github.com/ckeditor/ckeditor5-utils/issues/144

      this._listener.listenTo(this._listener, '_keydown:' + keyCode, function (evt, keyEvtData) {
        callback(keyEvtData, function () {
          // Stop the event in the DOM: no listener in the web page
          // will be triggered by this event.
          keyEvtData.preventDefault();
          keyEvtData.stopPropagation(); // Stop the event in the KeystrokeHandler: no more callbacks
          // will be executed for this keystroke.

          evt.stop();
        }); // Mark this keystroke as handled by the callback. See: #press.

        evt.return = true;
      }, {
        priority: priority
      });
    }
    /**
     * Triggers a keystroke handler for a specified key combination, if such a keystroke was {@link #set defined}.
     *
     * @param {module:engine/view/observer/keyobserver~KeyEventData} keyEvtData Key event data.
     * @returns {Boolean} Whether the keystroke was handled.
     */

  }, {
    key: "press",
    value: function press(keyEvtData) {
      return !!this._listener.fire('_keydown:' + getCode(keyEvtData), keyEvtData);
    }
    /**
     * Destroys the keystroke handler.
     */

  }, {
    key: "destroy",
    value: function destroy() {
      this._listener.stopListening();
    }
  }]);

  return KeystrokeHandler;
}();

export { KeystrokeHandler as default };